import { MIN_CURVE_ANGLE, MAX_CURVE_ANGLE, SHAPE_TYPES } from './MeasureConstants';
//
// Forge Viewer edge primitive helpers.
//
// Utilities to classify edge loops into simple primitives (circles, arcs, lines)
// using THREE.Vector3 operations that are available within Autodesk Forge Viewer
// extensions. The implementation ports the high-level logic from the C++
// EdgePrimitiveUtils helpers used in ElephantVertexGlm.

let THREE;
if (typeof globalThis !== 'undefined' && globalThis.THREE) {
  THREE = globalThis.THREE;
} else {
  try {
    // eslint-disable-next-line global-require
    THREE = require('three');
  } catch (error) {
    throw new Error('THREE dependency not resolved; provide global THREE or install the three package.');
  }
}

const TAU = Math.PI * 2;
const MIN_CIRCLE_EDGES = 7;
const MIN_ARC_EDGES = 3;
const EPSILON_PRECISION = 1e-3;
const VERTEX_PRECISION_PERCENT = 0.01; // 1%
const EDGE_LENGTH_TOLERANCE_FRACTION = 0.2; // 20%

/**
 * @typedef {Object} ArcPrimitive
 * @property {string} type "ARC"
 * @property {THREE.Vector3[]} points Sample points along the arc span
 * @property {THREE.Vector3} center Arc center
 * @property {number} radius Arc radius
 * @property {THREE.Vector3} normal Arc normal (unit vector)
 * @property {number} sweep Signed sweep angle in radians
 */

/**
 * @typedef {Object} LinePrimitive
 * @property {string} type "LINE"
 * @property {THREE.Vector3} start Start vertex
 * @property {THREE.Vector3} end End vertex
 * @property {number} length Segment length
 */

/**
 * @typedef {Object} CirclePrimitive
 * @property {string} type "CIRCLE"
 * @property {THREE.Vector3[]} points Loop vertices
 * @property {THREE.Vector3} center Circle center
 * @property {number} radius Circle radius
 * @property {number} circumference 2πr
 */

/**
 * Attempts to compute the centroid and ensure all consecutive edges share a
 * consistent length within tolerance (mirrors
 * getCentroidRSLForEqualAndValidEdges from the C++ implementation).
 *
 * @param {THREE.Vector3[]} vertices Ordered vertices (edges abut each other)
 * @param {number} minTolerance Absolute tolerance for length comparison
 * @returns {{centroid:THREE.Vector3|null, edgeLength:number}}
 */
function getCentroidForEqualAndValidEdges(vertices, minTolerance) {
  if (!Array.isArray(vertices) || vertices.length < 2) {
    return { centroid: null, edgeLength: 0 };
  }

  const firstEdgeLength = vertices[0].distanceTo(vertices[1]);
  if (firstEdgeLength <= EPSILON_PRECISION) {
    return { centroid: null, edgeLength: 0 };
  }

  const tolerance = Math.max(firstEdgeLength * EDGE_LENGTH_TOLERANCE_FRACTION, minTolerance);
  const sum = new THREE.Vector3().copy(vertices[0]);
  let prev = vertices[1];

  for (let i = 2; i < vertices.length; i += 1) {
    const current = vertices[i];
    const length = prev.distanceTo(current);
    if (Math.abs(length - firstEdgeLength) > tolerance) {
      return { centroid: null, edgeLength: 0 };
    }
    sum.add(prev);
    prev = current;
  }
  sum.add(prev);

  const centroid = sum.divideScalar(vertices.length);
  return { centroid, edgeLength: firstEdgeLength };
}

/**
 * Checks that all vertices are roughly equidistant from a reference point.
 */
function equidistantFromPoint(vertices, center, radius, minTolerance) {
  const tolerance = Math.max(radius * VERTEX_PRECISION_PERCENT, minTolerance);
  for (const vertex of vertices) {
    const distance = vertex.distanceTo(center);
    if (Math.abs(distance - radius) > tolerance) {
      return false;
    }
  }
  return true;
}

function orientedAngle(vA, vB, refVec) {
  const cross = new THREE.Vector3().crossVectors(vA, vB);
  const sin = cross.dot(refVec);
  const cos = vA.dot(vB);
  return Math.atan2(sin, cos);
}

function rotateVectorAroundAxis(vector, axis, angle) {
  const quaternion = new THREE.Quaternion().setFromAxisAngle(axis, angle);
  return vector.clone().applyQuaternion(quaternion);
}

function cloneVertices(vertices) {
  return vertices.map((vertex) => vertex.clone());
}

/**
 * Attempts to classify span vertices as a circular arc. Port of
 * EdgeCircularArc::circularArcFromSpanRSLPoints.
 *
 * @param {THREE.Vector3[]} spanVertices Ordered vertices along the curve span
 * @param {Object} options
 * @param {number} options.minTolerance Absolute tolerance used for comparisons
 * @returns {ArcPrimitive|null}
 */
function circularArcFromSpanVertices(spanVertices, { minTolerance = EPSILON_PRECISION } = {}) {
  if (!Array.isArray(spanVertices) || spanVertices.length < MIN_ARC_EDGES) {
    return null;
  }

  const { centroid: refPoint, edgeLength } = getCentroidForEqualAndValidEdges(spanVertices, minTolerance);
  if (!refPoint) {
    return null;
  }

  const pt1 = spanVertices[0];
  const pt2 = spanVertices[1];
  const refVec = new THREE.Vector3().
  subVectors(pt1, refPoint).
  normalize().
  cross(new THREE.Vector3().subVectors(pt2, refPoint).normalize()).
  normalize();

  if (refVec.lengthSq() <= EPSILON_PRECISION) {
    return null;
  }

  const pt3 = spanVertices[2];
  const vA = new THREE.Vector3().subVectors(pt2, pt1).normalize();
  const vB = new THREE.Vector3().subVectors(pt3, pt2).normalize();
  const interiorAngle = orientedAngle(vB, vA, refVec);

  const denominator = Math.PI - interiorAngle;
  if (Math.abs(denominator) < EPSILON_PRECISION) {
    return null;
  }

  const numSides = Math.round(TAU / denominator);
  if (numSides < MIN_CIRCLE_EDGES) {
    return null;
  }

  const circumRadius = edgeLength / 2 / Math.sin(Math.PI / numSides);
  if (!Number.isFinite(circumRadius) || circumRadius <= EPSILON_PRECISION) {
    return null;
  }

  const edgeVec = new THREE.Vector3().subVectors(pt2, pt1).normalize();
  const angleBisector = rotateVectorAroundAxis(edgeVec, refVec, interiorAngle / 2);
  const center = pt1.clone().add(angleBisector.clone().multiplyScalar(circumRadius));

  if (!equidistantFromPoint(spanVertices, center, circumRadius, minTolerance)) {
    return null;
  }

  const normal = refVec.clone();
  const startVec = new THREE.Vector3().subVectors(pt1, center);
  const endVec = new THREE.Vector3().subVectors(spanVertices[spanVertices.length - 1], center);
  const sweep = orientedAngle(startVec.clone().normalize(), endVec.clone().normalize(), normal);

  return {
    type: 'ARC',
    points: cloneVertices(spanVertices),
    center,
    radius: circumRadius,
    normal,
    sweep
  };
}

/**
 * Simplistic circle detection mirroring the reference `detectCircleFromLoop`.
 */
function circleFromLoop(vertices, { minTolerance = EPSILON_PRECISION, closed = true } = {}) {
  const edges = vertices.length - 1;
  if (!closed || edges < MIN_CIRCLE_EDGES) {
    return null;
  }

  const { centroid, edgeLength } = getCentroidForEqualAndValidEdges(vertices, minTolerance);
  if (!centroid) {
    return null;
  }

  const circumRadius = edgeLength / 2 / Math.sin(Math.PI / edges);
  if (!equidistantFromPoint(vertices, centroid, circumRadius, minTolerance)) {
    return null;
  }

  return {
    type: 'CIRCLE',
    points: cloneVertices(vertices),
    center: centroid.clone(),
    radius: circumRadius,
    circumference: TAU * circumRadius
  };
}

/**
 * Placeholder for ellipse detection – returns null but keeps the call site
 * compatible with the C++ flow (extend as needed).
 */
function ellipseFromLoop() {
  return null;
}

/**
 * Breaks a loop into simple primitives (arcs and lines). This follows the
 * greedy approach used in `EdgeSimpleCurve::simpleCurvesAndLinesFromEdges` by
 * attempting the largest qualifying arc span starting from the current edge.
 */
function simpleCurvesAndLinesFromVertices(vertices, { minTolerance = EPSILON_PRECISION } = {}) {
  if (!Array.isArray(vertices) || vertices.length < 2) {
    return [];
  }

  const primitives = [];
  let startIndex = 0;

  while (startIndex < vertices.length - 1) {
    let arcPrimitive = null;
    let bestEndIndex = startIndex + 1;

    for (let end = vertices.length; end >= startIndex + MIN_ARC_EDGES; end -= 1) {
      const span = vertices.slice(startIndex, end);
      const arc = circularArcFromSpanVertices(span, { minTolerance });
      if (arc) {
        arcPrimitive = arc;
        bestEndIndex = end - 1;
        break;
      }
    }

    if (arcPrimitive) {
      primitives.push(arcPrimitive);
      startIndex = bestEndIndex;
    } else {
      const start = vertices[startIndex];
      const end = vertices[startIndex + 1];
      primitives.push({
        type: 'LINE',
        start: start.clone(),
        end: end.clone(),
        length: start.distanceTo(end)
      });
      startIndex += 1;
    }
  }

  return primitives;
}

/**
 * Computes the central angle of an arc (always positive, handles reflex angles).
 * The central angle is measured from the arc's start point to its end point,
 * going counter-clockwise relative to the reference normal vector.
 *
 * @param {THREE.Vector3} start - Start point of arc
 * @param {THREE.Vector3} end - End point of arc
 * @param {THREE.Vector3} center - Center of arc
 * @param {THREE.Vector3} refVec - Normal vector defining CCW direction
 * @returns {number} Central angle in radians [0, 2π]
 */
function centralAngle(start, end, center, refVec) {
  const v1 = new THREE.Vector3().subVectors(center, start).normalize();
  const v2 = new THREE.Vector3().subVectors(end, center).normalize();
  const angle = orientedAngle(v1, v2, refVec);
  return angle > 0 ? angle : 2 * Math.PI + angle;
}

/**
 * Checks if the angle and length of an edge are within curve tolerances.
 * Used to determine if consecutive edges belong to the same curve span.
 *
 * @param {THREE.Vector3} currVec - Current edge vector
 * @param {THREE.Vector3} angleVec - Previous edge vector for angle comparison
 * @param {THREE.Vector3} orientVec - Orientation vector for angle sign determination
 * @param {number|null} refLength - Reference edge length (optional)
 * @param {number|null} tolerance - Length tolerance (optional)
 * @returns {{angleOk: boolean, lengthOk: boolean}} Validation results
 */
function checkAngleLengthTolerances(currVec, angleVec, orientVec, refLength, tolerance) {
  let angleOk = true;
  let lengthOk = true;

  const currNorm = currVec.clone().normalize();
  const angleNorm = angleVec.clone().normalize();
  const angle = orientedAngle(currNorm, angleNorm, orientVec);

  // Check if angle is within acceptable curve range
  if (angle < MIN_CURVE_ANGLE || angle > MAX_CURVE_ANGLE) {
    angleOk = false;
  }

  // Check if edge length is within tolerance of reference
  if (refLength !== null && tolerance !== null) {
    if (Math.abs(currVec.length() - refLength) > tolerance) {
      lengthOk = false;
    }
  }

  return { angleOk, lengthOk };
}

/**
 * Computes the centroid and validates equal edge lengths for a set of points.
 * Returns null if edges have inconsistent lengths (not suitable for arc fitting).
 *
 * @param {THREE.Vector3[]} points - Array of vertices
 * @param {number} minTolerance - Minimum tolerance
 * @returns {{centroid: THREE.Vector3|null, edgeLength: number}} Centroid and reference edge length
 */
function getCentroidForEqualEdges(points, minTolerance) {
  if (points.length < 2) {
    return { centroid: null, edgeLength: 0 };
  }

  const pt1 = points[0];
  const pt2 = points[1];
  const firstEdgeLength = pt1.distanceTo(pt2);

  if (firstEdgeLength <= EPSILON_PRECISION) {
    return { centroid: null, edgeLength: 0 };
  }

  const tolerance = Math.max(firstEdgeLength * EDGE_LENGTH_TOLERANCE_FRACTION, minTolerance);
  const centroid = new THREE.Vector3();

  // Validate all edges have similar length while accumulating centroid
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const edgeLength = p1.distanceTo(p2);

    if (Math.abs(edgeLength - firstEdgeLength) > tolerance) {
      return { centroid: null, edgeLength: 0 };
    }
    centroid.add(p1);
  }
  centroid.add(points[points.length - 1]);
  centroid.divideScalar(points.length);

  return { centroid, edgeLength: firstEdgeLength };
}

/**
 * Finds a span of consecutive edges that could form a curve.
 * Edges are grouped if they have similar lengths and interior angles
 * within the acceptable curve range [MIN_CURVE_ANGLE, MAX_CURVE_ANGLE].
 *
 * @param {THREE.Vector3[]} vertices - Array of vertices
 * @param {number} startIdx - Starting vertex index
 * @param {number} lastIdx - Last valid vertex index (numVertices - 1)
 * @param {number} minTolerance - Minimum tolerance for comparisons
 * @returns {{spanStart: number, spanEnd: number}} Start and end indices of the curve span
 */
function getCurveSpan(vertices, startIdx, lastIdx, minTolerance) {
  let endIdx = startIdx + 1;

  const pt1 = vertices[startIdx];
  const pt2 = vertices[startIdx + 1];
  const firstEdgeLength = pt1.distanceTo(pt2);
  const tolerance = Math.max(firstEdgeLength * EDGE_LENGTH_TOLERANCE_FRACTION, minTolerance);

  let prevPt = pt1.clone();
  let currPt = pt2.clone();

  // Need at least one more edge to check angles
  if (startIdx + 1 < lastIdx) {
    // Compute the arc-plane normal. Using only the first 3 vertices can produce a
    // near-zero cross product when they are nearly collinear (finely tessellated arcs).
    // Scan forward until we find a vertex far enough off the pt1→pt2 line to give a
    // stable normal; the threshold is scale-invariant (perpendicular distance > ε).
    const edge = new THREE.Vector3().subVectors(pt2, pt1);
    const edgeLenSq = edge.lengthSq();
    let refVec = new THREE.Vector3();
    const scratch = new THREE.Vector3();
    const cross = new THREE.Vector3();
    for (let k = startIdx + 2; k <= lastIdx; k++) {
      scratch.subVectors(vertices[k], pt1);
      cross.crossVectors(edge, scratch);
      if (cross.lengthSq() > EPSILON_PRECISION * EPSILON_PRECISION * edgeLenSq) {
        refVec.copy(cross).normalize();
        break;
      }
    }

    if (refVec.lengthSq() === 0) {
      // All remaining vertices are collinear — no arc possible from here.
      return { spanStart: startIdx, spanEnd: endIdx };
    }

    // Extend span forward while edges satisfy curve constraints
    for (let nextIdx = startIdx + 2; nextIdx <= lastIdx; nextIdx++) {
      const nextPt = vertices[nextIdx];
      const vec1 = prevPt.clone().sub(currPt);
      const vec2 = nextPt.clone().sub(currPt);

      const { angleOk, lengthOk } = checkAngleLengthTolerances(
        vec2, vec1, refVec, firstEdgeLength, tolerance
      );

      if (!angleOk || !lengthOk) {
        break;
      }

      endIdx++;
      prevPt.copy(currPt);
      currPt.copy(nextPt);
    }
  }

  return { spanStart: startIdx, spanEnd: endIdx };
}

/**
 * Attempts to fit a circular arc to a span of vertices.
 * Uses the inscribed polygon method to compute the circumradius and center.
 *
 * @param {THREE.Vector3[]} vertices - Full vertex array
 * @param {number} startIdx - Start index of span
 * @param {number} endIdx - End index of span (inclusive, refers to last edge's end point)
 * @param {number} minTolerance - Minimum tolerance
 * @returns {Object|null} Arc object with type, centroid, vertices, perimeter; or null if fitting fails
 */
function tryFitCircularArc(vertices, startIdx, endIdx, minTolerance) {
  // Extract span vertices (endIdx + 1 because endIdx is edge index, we need the endpoint)
  const spanVertices = vertices.slice(startIdx, endIdx + 2);
  const numEdges = spanVertices.length - 1;

  if (numEdges < MIN_ARC_EDGES) {
    return null;
  }

  // Validate equal edge lengths and get centroid
  const { centroid, edgeLength } = getCentroidForEqualEdges(spanVertices, minTolerance);
  if (!centroid || edgeLength === 0) {
    return null;
  }

  // Compute reference vector (normal to arc plane) from first two vertices
  const pt1 = spanVertices[0];
  const pt2 = spanVertices[1];
  const v1 = pt1.clone().sub(centroid).normalize();
  const v2 = pt2.clone().sub(centroid).normalize();
  const refVec = new THREE.Vector3().crossVectors(v1, v2).normalize();

  if (refVec.lengthSq() <= EPSILON_PRECISION) {
    return null;
  }

  // Compute interior angle from first 3 points
  const pt3 = spanVertices[2];
  const vA = pt2.clone().sub(pt1).normalize();
  const vB = pt2.clone().sub(pt3).normalize();
  const interiorAngle = orientedAngle(vB, vA, refVec);

  // Calculate number of sides if this were a closed regular polygon
  const numSides = Math.round(2 * Math.PI / (Math.PI - interiorAngle));
  if (!Number.isFinite(numSides) || numSides < MIN_CIRCLE_EDGES) {
    return null;
  }

  // Calculate circumradius: r = (edgeLength / 2) / sin(π / numSides)
  const circumRadius = edgeLength / 2 / Math.sin(Math.PI / numSides);
  if (!Number.isFinite(circumRadius) || circumRadius <= EPSILON_PRECISION) {
    return null;
  }

  // Calculate center using angle bisector method
  const edgeVec = pt2.clone().sub(pt1).normalize();
  const quaternion = new THREE.Quaternion().setFromAxisAngle(refVec, interiorAngle / 2);
  const angleBisector = edgeVec.clone().applyQuaternion(quaternion);
  const center = pt1.clone().add(angleBisector.multiplyScalar(circumRadius));

  // Validate all points are equidistant from center
  if (!equidistantFromPoint(spanVertices, center, circumRadius, minTolerance)) {
    return null;
  }

  // Calculate arc perimeter using central angle
  const arcPerimeter = circumRadius * centralAngle(
    spanVertices[0],
    spanVertices[spanVertices.length - 1],
    center,
    refVec
  );

  return {
    type: SHAPE_TYPES.CIRCULAR_ARC,
    centroid: spanVertices[Math.floor(spanVertices.length / 2)],
    vertices: spanVertices,
    perimeter: arcPerimeter
  };
}

/**
 * Creates line primitives from a span of vertices.
 * Each edge in the span becomes a separate line primitive.
 *
 * @param {THREE.Vector3[]} vertices - Full vertex array
 * @param {number} startIdx - Inclusive start vertex index
 * @param {number} endIdx - Inclusive end vertex index
 * @returns {Object[]} Array of LINE primitives
 */
function createLinesFromSpan(vertices, startIdx, endIdx) {
  const lines = [];

  for (let i = startIdx; i < endIdx; i++) {
    const p1 = vertices[i];
    const p2 = vertices[i + 1];
    const length = p1.distanceTo(p2);

    if (length > EPSILON_PRECISION) {
      lines.push({
        type: SHAPE_TYPES.LINE,
        vertices: [p1.clone(), p2.clone()]
      });
    }
  }

  return lines;
}

/**
 * Creates a simple curve primitive covering vertices [startIdx, endIdx] inclusive.
 *
 * @param {THREE.Vector3[]} vertices - Full vertex array
 * @param {number} startIdx - Inclusive start vertex index
 * @param {number} endIdx - Inclusive end vertex index
 * @returns {Object|null}
 */
function createSimpleCurve(vertices, startIdx, endIdx) {
  if (endIdx <= startIdx) return null;
  const curveVertices = [];
  let perimeter = 0;
  for (let i = startIdx; i <= endIdx; i++) {
    curveVertices.push(vertices[i].clone());
    if (i > startIdx) perimeter += vertices[i].distanceTo(vertices[i - 1]);
  }
  if (perimeter <= EPSILON_PRECISION) return null;
  return {
    type: SHAPE_TYPES.SIMPLE_CURVE,
    centroid: curveVertices[Math.floor(curveVertices.length / 2)],
    vertices: curveVertices,
    perimeter: perimeter
  };
}

/**
 * Detects geometric primitives (lines and circular arcs) from an array of 3D vertices.
 *
 * Algorithm overview (Port of C++ algorithm of EdgeCircularArc::circularArcFromSpanRSLPoints):
 * 1. Iterate through vertices looking for spans of edges with consistent geometry
 * 2. For each span, check if edges have similar lengths and smooth angle transitions
 * 3. If span has enough edges, attempt to fit a circular arc (using the inscribed polygon method)
 * 4. If arc fitting fails or span is too short, output edges as individual lines
 *
 * @param {THREE.Vector3[]} vertices - Array of THREE.Vector3 points forming an open polyline (edges abut each other)
 * @param {Object} [options={}] - Configuration options
 * @param {number} [options.tolerance=0.001] - Minimum tolerance for geometric comparisons
 * @returns {Object[]} Array of detected primitives (lines and circular arcs)
 */
function detectCurvesAndLines(vertices, options = {}) {
  const minTolerance = options.tolerance ?? EPSILON_PRECISION;
  const primitives = [];

  if (!vertices || vertices.length < 2) {
    return primitives;
  }

  const numEdges = vertices.length - 1;

  // If too few edges for arc detection, output all as lines
  if (numEdges < MIN_ARC_EDGES) {
    return createLinesFromSpan(vertices, 0, numEdges);
  }

  let startEdgeIdx = 0;
  const lastEdgeIdx = numEdges - 1;

  while (startEdgeIdx <= lastEdgeIdx) {
    const { spanStart, spanEnd } = getCurveSpan(vertices, startEdgeIdx, lastEdgeIdx, minTolerance);
    const spanEdgeCount = spanEnd - spanStart;

    if (spanEdgeCount < MIN_ARC_EDGES) {
      // Not enough edges for an arc, output as lines
      primitives.push(...createLinesFromSpan(vertices, spanStart, spanEnd));
    } else {
      const arc = tryFitCircularArc(vertices, spanStart, spanEnd, minTolerance);
      if (arc) {
        primitives.push(arc);
      } else {
        const curve = createSimpleCurve(vertices, spanStart, spanEnd);
        if (curve) primitives.push(curve);
      }
    }

    startEdgeIdx = spanEnd;
  }

  return primitives;
}

/**
 * Checks if the surface is simpler than supported shapes.
 * @param {THREE.Vector3[]} vertices - The vertices of the shape
 * @returns {boolean} True if the surface is simpler than supported shapes, false otherwise
 */
function isSimpleSurface(vertices) {
  return vertices.length < MIN_CIRCLE_EDGES;
}

/**
 * Detects a circle from a list of vertices. This function is used to detect a circle from a list of vertices.
 * @param {THREE.Vector3[]} vertices - The vertices of the shape
 * @returns {Object} The detected shape
 */
// TODO check if loop is coplanar
function detectCircleFromLoop(vertices) {
  // if the surface is simpler than a circle, return null.
  if (isSimpleSurface(vertices)) {
    return null;
  }
  const centroid = getCentroid(vertices);
  const referenceRadius = vertices[0].distanceTo(centroid);
  if (referenceRadius <= EPSILON_PRECISION) {
    return null;
  }

  // Use for...of with early return instead of forEach
  for (const vertex of vertices) {
    const radius = vertex.distanceTo(centroid);
    if (Math.abs(radius - referenceRadius) > referenceRadius * EPSILON_PRECISION) {
      return null;
    }
  }

  return {
    type: SHAPE_TYPES.CIRCLE,
    centroid: centroid,
    vertices: vertices,
    perimeter: 2 * Math.PI * referenceRadius,
    radius: referenceRadius
  };
}

/**
 * Detects an ellipse from a list of vertices.
 * @param {THREE.Vector3[]} vertices - The vertices of the shape
 * @returns {Object} The detected shape
 */
function detectEllipseFromLoop(vertices) {
  // if the surface is simpler than an ellipse, return null.
  if (isSimpleSurface(vertices)) {
    return null;
  }
  if (vertices.length % 4 !== 0) {
    return null;
  }

  // we assume that the points are in the order of a closed loop.
  // now we have to find the major and minor axes of the ellipse.
  // we can do this by finding the two points that are farthest apart.
  // the major axis is the line segment between these two points.
  // the minor axis is the line segment perpendicular to the major axis but will be checked by the other two points.
  let minDistance = Number.MAX_VALUE;
  let maxDistance = 0;
  const halfVerticesLength = vertices.length / 2;
  let majorAxisIndex;
  let minorAxisIndex;

  for (let i = 0; i < halfVerticesLength; i++) {
    const oppositeIndex = i + halfVerticesLength;
    const distance = vertices[i].distanceTo(vertices[oppositeIndex]);

    minDistance = Math.min(minDistance, distance);
    maxDistance = Math.max(maxDistance, distance);
    if (distance === maxDistance) {
      majorAxisIndex = i;
    }
    if (distance === minDistance) {
      minorAxisIndex = i;
    }
  }

  const majorAxis = vertices[majorAxisIndex].clone().sub(vertices[(majorAxisIndex + halfVerticesLength) % vertices.length]);
  const minorAxis = vertices[minorAxisIndex].clone().sub(vertices[(minorAxisIndex + halfVerticesLength) % vertices.length]);

  minorAxis.normalize();
  majorAxis.normalize();
  // check if the minor axis is perpendicular to the major axis
  if (Math.abs(minorAxis.dot(majorAxis)) > EPSILON_PRECISION) {
    return null;
  }

  // check if the distance between the two points is not zero
  if (maxDistance === 0) {
    return null; // All points are the same, not an ellipse.
  }

  // calculate the distance between the centroid and the major and minor axes
  const ellipseMajorAxisDistance = maxDistance / 2;
  const ellipseMinorAxisDistance = minDistance / 2;
  // calculate the centroid of the ellipse
  const ellipseCenter = getCentroid(vertices);

  // get foci points of the ellipse
  const ellipseFociPoints = getFociPoints(ellipseMajorAxisDistance, ellipseMinorAxisDistance, ellipseCenter, majorAxis);

  // verify that the shape is an ellipse by summing lengths of the lines between the foci points and the vertices.
  const locus = maxDistance;
  for (let i = 0; i < vertices.length; i++) {
    const currentVertex = vertices[i];
    const f1 = ellipseFociPoints.f1;
    const f2 = ellipseFociPoints.f2;
    const distanceToF1 = currentVertex.distanceTo(f1);
    const distanceToF2 = currentVertex.distanceTo(f2);
    if (Math.abs(distanceToF1 + distanceToF2 - locus) > EPSILON_PRECISION) {
      return null;
    }
  }
  const a = ellipseMajorAxisDistance;
  const b = ellipseMinorAxisDistance;
  const ellipseCircumference = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));

  if (!ellipseFociPoints || !ellipseCenter || !ellipseMajorAxisDistance || !ellipseMinorAxisDistance || !ellipseCircumference) {
    return null;
  }

  const ellipse = {
    type: SHAPE_TYPES.ELLIPSE,
    centroid: ellipseCenter,
    vertices: vertices,
    perimeter: ellipseCircumference,
    a: ellipseMajorAxisDistance, // distance between centroid and the major axis
    b: ellipseMinorAxisDistance, // distance between centroid and the minor axis
    majorAxis: majorAxis,
    minorAxis: minorAxis
  };

  return ellipse;
}

/**
 * Calculates the foci points of an ellipse
 * @param {number} semiMajorAxis - The length of the semi-major axis
 * @param {number} semiMinorAxis - The length of the semi-minor axis
 * @param {THREE.Vector3} centroid - The centroid of the ellipse
 * @param {THREE.Vector3} majorAxis - The normalized vector of the major axis
 * @returns {Object} The foci points
 */
const getFociPoints = (semiMajorAxis, semiMinorAxis, centroid, majorAxis) => {
  // based on Linear Eccentricity https://en.wikipedia.org/wiki/Ellipse#Linear_eccentricity
  const c = Math.sqrt(semiMajorAxis * semiMajorAxis - semiMinorAxis * semiMinorAxis);
  const f1 = centroid.clone().add(majorAxis.clone().multiplyScalar(c));
  const f2 = centroid.clone().sub(majorAxis.clone().multiplyScalar(c));
  return { f1, f2 };
};

/**
 * Calculates the centroid of a list of vertices
 * @param {THREE.Vector3[]} vertices - The vertices to calculate the centroid of
 * @returns {THREE.Vector3} The centroid of the vertices
 */
const getCentroid = (vertices) => {
  const centroid = new THREE.Vector3();
  vertices.forEach((vertex) => {
    centroid.add(vertex);
  });
  centroid.divideScalar(vertices.length);
  return centroid;
};

/**
 * High-level helper equivalent to `getPrimitivesForLoop` in the C++ code.
 */
function getPrimitivesForLoop(vertices, { closed = true, minTolerance = EPSILON_PRECISION } = {}) {
  // if (!Array.isArray(vertices) || vertices.length === 0) {
  //     return [];
  // }

  // const circle = circleFromLoop(vertices, { closed, minTolerance });
  // if (circle) {
  //     return [circle];
  // }

  // const ellipse = ellipseFromLoop(vertices, { closed, minTolerance });
  // if (ellipse) {
  //     return [ellipse];
  // }

  return simpleCurvesAndLinesFromVertices(vertices, { minTolerance });
}

export {
  getPrimitivesForLoop,
  detectCurvesAndLines,
  detectCircleFromLoop,
  detectEllipseFromLoop,
  getCentroid,
  isSimpleSurface,
  getFociPoints,
  orientedAngle,
  centralAngle,
  equidistantFromPoint,
  getCentroidForEqualEdges,
  getCurveSpan,
  tryFitCircularArc,
  createLinesFromSpan,
  createSimpleCurve };